import { useState, useEffect } from 'react';
import { Category } from '@/types/assessment';
import { useToast } from '@/hooks/use-toast';
import { validateAllRequiredQuestionsAnswered } from './responseUtils';
import { 
  AssessmentStep, 
  PersonalInfo, 
  UseAssessmentProgressProps, 
  UseAssessmentProgressResult 
} from './types/progressTypes';
import { 
  calculateTotalQuestions,
  calculateCompletedQuestionsCount,
  calculateNavigationProgressPercentage,
  saveProgressToLocalStorage,
  loadProgressFromLocalStorage,
  clearProgressFromLocalStorage
} from './progressUtils';

export function useAssessmentProgress({ 
  assessment, 
  categories 
}: UseAssessmentProgressProps): UseAssessmentProgressResult {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState<AssessmentStep>('welcome');
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1); // -1 means showing category intro
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    fullName: '',
    email: '',
    company: '',
    designation: '',
    consentGiven: false
  });
  
  // Calculate metrics
  const totalQuestions = calculateTotalQuestions(categories);
  const currentCategory = categories[currentCategoryIndex];
  const currentQuestion = currentCategory?.questions[currentQuestionIndex];
  const completedQuestionsCount = calculateCompletedQuestionsCount(
    categories, 
    currentCategoryIndex, 
    currentQuestionIndex
  );
  const navigationProgressPercentage = calculateNavigationProgressPercentage(
    totalQuestions,
    completedQuestionsCount
  );
  
  // Try to load saved progress from localStorage
  useEffect(() => {
    if (assessment?.id) {
      const savedState = loadProgressFromLocalStorage(assessment.id);
      setCurrentCategoryIndex(savedState.categoryIndex);
      setCurrentQuestionIndex(savedState.questionIndex);
      setCurrentStep(savedState.step);
      setPersonalInfo(savedState.personalInfo);
    }
  }, [assessment?.id]);
  
  // Save progress to localStorage whenever it changes
  useEffect(() => {
    if (assessment?.id) {
      saveProgressToLocalStorage(
        assessment.id,
        currentCategoryIndex,
        currentQuestionIndex,
        currentStep,
        personalInfo
      );
    }
  }, [assessment?.id, currentCategoryIndex, currentQuestionIndex, currentStep, personalInfo]);
  
  // Handle starting the assessment
  const handleStartAssessment = () => {
    setCurrentStep('questions');
    setCurrentCategoryIndex(0);
    setCurrentQuestionIndex(0); // Start with first question
  };
  
  // Handle resetting the assessment
  const handleRestartAssessment = () => {
    setCurrentCategoryIndex(0);
    setCurrentQuestionIndex(0);
    setCurrentStep('welcome');
    setPersonalInfo({
      fullName: '',
      email: '',
      company: '',
      designation: '',
      consentGiven: false
    });
    if (assessment?.id) {
      clearProgressFromLocalStorage(assessment.id);
    }
  };
  
  // Add updateAssessmentProgress function
  const updateAssessmentProgress = (progress: number) => {
    // This could be enhanced later to actually update some progress indicator
    console.log(`Assessment progress updated: ${progress}%`);
  };
  
  // Navigation functions
  const moveToNextQuestion = (responses: Record<string, any>) => {
    if (currentQuestionIndex < currentCategory?.questions.length - 1) {
      // Move to the next question in the current category
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (currentCategoryIndex < categories.length - 1) {
      // Move to the next category and start with first question
      setCurrentCategoryIndex(currentCategoryIndex + 1);
      setCurrentQuestionIndex(0);
    } else {
      // End of assessment, validate all required questions before moving to personal info
      const validation = validateAllRequiredQuestionsAnswered(responses, categories);
      
      if (!validation.valid) {
        // Show error toast with unanswered questions info
        toast({
          variant: "destructive",
          title: "Incomplete Assessment",
          description: `Please answer all required questions before submitting. (${validation.unansweredQuestions.length} questions remaining)`
        });
        
        // Stay on current page
        return;
      }
      
      // All required questions answered, move to personal info
      setCurrentStep('personalInfo');
    }
  };
  
  const moveToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      // Move to the previous question in the current category
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else if (currentCategoryIndex > 0) {
      // Move to the last question of the previous category
      setCurrentCategoryIndex(currentCategoryIndex - 1);
      setCurrentQuestionIndex(categories[currentCategoryIndex - 1].questions.length - 1);
    }
  };

  // Move to a specific category and question
  const navigateToQuestion = (categoryIndex: number, questionIndex: number) => {
    if (
      categoryIndex >= 0 && 
      categoryIndex < categories.length && 
      questionIndex >= 0 && 
      questionIndex < categories[categoryIndex].questions.length
    ) {
      setCurrentCategoryIndex(categoryIndex);
      setCurrentQuestionIndex(questionIndex);
    }
  };

  // Move back from personal info to last question
  const moveBackToQuestions = () => {
    setCurrentStep('questions');
    setCurrentCategoryIndex(categories.length - 1);
    setCurrentQuestionIndex(categories[categories.length - 1].questions.length - 1);
  };
  
  // Complete assessment (set to 'complete' step)
  const completeAssessment = () => {
    setCurrentStep('complete');
  };
  
  return {
    currentStep,
    currentCategoryIndex,
    currentQuestionIndex,
    currentCategory,
    currentQuestion,
    personalInfo,
    setPersonalInfo,
    navigationProgressPercentage,
    handleStartAssessment,
    handleRestartAssessment,
    moveToNextQuestion,
    moveToPreviousQuestion,
    moveBackToQuestions,
    navigateToQuestion,
    completeAssessment,
    updateAssessmentProgress
  };
}
